export const idlFactory = ({ IDL }) => {
  const GetBlocksResult = IDL.Rec()
  const ICRC3Value = IDL.Rec()
  const PoolId = IDL.Nat16
  const DexUpgradeArg = IDL.Variant({
    ProtocolFeePpm: IDL.Record({
      pool_id: PoolId,
      protocol_fee_ppm: IDL.Nat32,
    }),
    SwapFeePpm: IDL.Record({
      swap_fee_ppm: IDL.Nat32,
      pool_id: PoolId,
    }),
  })
  const UpgradeSupplyDetails = IDL.Variant({
    LB: IDL.Nat64,
    LT: IDL.Nat64,
    RF: IDL.Nat64,
    Cap: IDL.Nat64,
    LRF: IDL.Nat64,
    LTV: IDL.Nat64,
  })
  const TokenType = IDL.Variant({
    ICP: IDL.Null,
    WTN: IDL.Null,
    USDC: IDL.Null,
    USDT: IDL.Null,
    ckBTC: IDL.Null,
    nICP: IDL.Null,
    ckDOGE: IDL.Null,
    YUSAN: IDL.Null,
  })
  const KinkUtilizationCurveParams = IDL.Record({
    rate_start_e8s: IDL.Opt(IDL.Nat64),
    rate_kink_e8s: IDL.Nat64,
    kink_utilization_e8s: IDL.Nat64,
    rate_end_e8s: IDL.Nat64,
  })
  const BorrowRate = IDL.Variant({
    Kink: KinkUtilizationCurveParams,
    Fixed: IDL.Nat64,
  })
  const UpgradeBorrowDetails = IDL.Variant({
    Cap: IDL.Nat64,
    BorrowRate: BorrowRate,
  })
  const UpgradeArg = IDL.Variant({
    PriceRetryDelaySecs: IDL.Nat64,
    UpgradeSupplyDetails: IDL.Record({
      details: UpgradeSupplyDetails,
      token_type: TokenType,
    }),
    ActionValueFeeE8S: IDL.Nat64,
    UpgradeBorrowDetails: IDL.Record({
      details: UpgradeBorrowDetails,
      token_type: TokenType,
    }),
    PriceIntervalSecs: IDL.Nat64,
  })
  const ConfigUpgradeArg = IDL.Variant({
    PriceRetryDelaySecs: IDL.Nat64,
    ActionValueFeeE8S: IDL.Nat64,
    PriceMaxAgeMultiplier: IDL.Nat64,
    PriceIntervalSecs: IDL.Nat64,
  })
  const CanisterUpgradeArg = IDL.Variant({
    Dex: DexUpgradeArg,
    Lending: UpgradeArg,
    Config: ConfigUpgradeArg,
  })
  const CanisterInitArg = IDL.Record({
    xrc_canister_id: IDL.Principal,
    authorized_principals: IDL.Vec(IDL.Principal),
    upgrade_args: IDL.Vec(CanisterUpgradeArg),
  })
  const Args = IDL.Variant({
    Upgrade: IDL.Vec(CanisterUpgradeArg),
    Init: CanisterInitArg,
  })
  const DexSwapArgs = IDL.Record({
    min_amount_out: IDL.Nat64,
    token_in: TokenType,
    allow_partial_fill: IDL.Bool,
    amount_in: IDL.Nat64,
    pool_id: PoolId,
  })
  const DexAction = IDL.Variant({ Swap: DexSwapArgs })
  const Subaccount = IDL.Vec(IDL.Nat8)
  const Account = IDL.Record({
    owner: IDL.Principal,
    subaccount: IDL.Opt(Subaccount),
  })
  const BalanceSupplyArgs = IDL.Record({
    recipient: IDL.Opt(Account),
    from_subaccount: IDL.Opt(Subaccount),
    amount: IDL.Nat,
    token_type: TokenType,
  })
  const AmountOrShares = IDL.Variant({
    Amount: IDL.Nat64,
    Shares: IDL.Nat64,
  })
  const WithdrawArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_or_shares: AmountOrShares,
    token_type: TokenType,
  })
  const SupplyPoolArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const ToggleCollateralArgs = IDL.Record({
    collateral_enabled: IDL.Bool,
    subaccount: IDL.Opt(Subaccount),
    token_type: TokenType,
  })
  const WithdrawPoolArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const RepayArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const BorrowArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const SupplyArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_or_shares: AmountOrShares,
    token_type: TokenType,
  })
  const LendingAction = IDL.Variant({
    Withdraw: WithdrawArgs,
    SupplyPool: SupplyPoolArgs,
    ToggleCollateral: ToggleCollateralArgs,
    WithdrawPool: WithdrawPoolArgs,
    Repay: RepayArgs,
    Borrow: BorrowArgs,
    Supply: SupplyArgs,
  })
  const FeeMode = IDL.Variant({
    Exclusive: IDL.Null,
    Inclusive: IDL.Null,
  })
  const BalanceWithdrawArgs = IDL.Record({
    to: Account,
    from_subaccount: IDL.Opt(Subaccount),
    fee_mode: IDL.Opt(FeeMode),
    amount: IDL.Nat,
    token_type: TokenType,
  })
  const Action = IDL.Variant({
    Dex: DexAction,
    BalanceSupply: BalanceSupplyArgs,
    Panic: IDL.Null,
    Lending: LendingAction,
    BalanceWithdraw: BalanceWithdrawArgs,
  })
  const ActionsArgs = IDL.Record({
    actions: IDL.Vec(Action),
    fail_fast: IDL.Bool,
  })
  const DexSwapResult = IDL.Record({
    fee_e8s: IDL.Nat64,
    amount_out: IDL.Nat64,
    amount_in: IDL.Nat64,
  })
  const DexResponse = IDL.Variant({ Swap: DexSwapResult })
  const BalanceResponse = IDL.Record({
    tx_id: IDL.Nat,
    new_balance: IDL.Nat,
  })
  const AnyResponse = IDL.Variant({
    DexResponse: DexResponse,
    LendingResponse: IDL.Nat64,
    BalanceResponse: BalanceResponse,
  })
  const DexBalanceError = IDL.Variant({
    InsufficientBalance: IDL.Record({
      requested: IDL.Nat64,
      available: IDL.Nat64,
    }),
    BalanceUnderflow: IDL.Null,
    BalanceOverflow: IDL.Null,
  })
  const DexError = IDL.Variant({
    Fee: IDL.Null,
    PositionNotFound: IDL.Null,
    InsufficientShares: IDL.Null,
    ZeroLiquidity: IDL.Null,
    PoolNotFound: IDL.Null,
    StakeNotFound: IDL.Null,
    InvalidTokenIn: IDL.Null,
    InvalidPriceRange: IDL.Null,
    NoLiquidity: IDL.Null,
    InsufficientLiquidity: IDL.Null,
    StaleOracle: IDL.Null,
    SlippageExceeded: IDL.Null,
    BalanceError: DexBalanceError,
    AccessError: IDL.Text,
  })
  const TransferError = IDL.Variant({
    GenericError: IDL.Record({
      message: IDL.Text,
      error_code: IDL.Nat,
    }),
    TemporarilyUnavailable: IDL.Null,
    BadBurn: IDL.Record({ min_burn_amount: IDL.Nat }),
    Duplicate: IDL.Record({ duplicate_of: IDL.Nat }),
    BadFee: IDL.Record({ expected_fee: IDL.Nat }),
    CreatedInFuture: IDL.Record({ ledger_time: IDL.Nat64 }),
    TooOld: IDL.Null,
    InsufficientFunds: IDL.Record({ balance: IDL.Nat }),
  })
  const WithdrawError = IDL.Variant({
    InsufficientLiquidity: IDL.Record({
      requested: IDL.Nat64,
      available: IDL.Nat64,
    }),
  })
  const TransferFromError = IDL.Variant({
    GenericError: IDL.Record({
      message: IDL.Text,
      error_code: IDL.Nat,
    }),
    TemporarilyUnavailable: IDL.Null,
    InsufficientAllowance: IDL.Record({ allowance: IDL.Nat }),
    BadBurn: IDL.Record({ min_burn_amount: IDL.Nat }),
    Duplicate: IDL.Record({ duplicate_of: IDL.Nat }),
    BadFee: IDL.Record({ expected_fee: IDL.Nat }),
    CreatedInFuture: IDL.Record({ ledger_time: IDL.Nat64 }),
    TooOld: IDL.Null,
    InsufficientFunds: IDL.Record({ balance: IDL.Nat }),
  })
  const BorrowError = IDL.Variant({
    LTVTooHigh: IDL.Record({ actual: IDL.Nat64, expected: IDL.Nat64 }),
    BorrowCapExceeded: IDL.Record({
      actual: IDL.Nat64,
      expected: IDL.Nat64,
    }),
    NotEnoughCollateral: IDL.Record({
      actual: IDL.Nat64,
      expected: IDL.Nat64,
    }),
  })
  const InternalError = IDL.Variant({
    Overflow: IDL.Null,
    ConversionError: IDL.Null,
  })
  const BalanceError = IDL.Variant({
    InsufficientBalance: IDL.Record({
      requested: IDL.Nat64,
      available: IDL.Nat64,
    }),
    BalanceUnderflow: IDL.Null,
    BalanceOverflow: IDL.Null,
  })
  const LendingError = IDL.Variant({
    Fee: IDL.Null,
    InsufficientShares: IDL.Record({
      actual: IDL.Nat64,
      expected: IDL.Nat64,
    }),
    TokenAlreadyExists: TokenType,
    SupplyCapExceeded: IDL.Null,
    TransferError: TransferError,
    LedgerMetadataFetchFailed: IDL.Record({
      token_type: TokenType,
      reason: IDL.Text,
    }),
    NoDebt: IDL.Null,
    NoSupply: IDL.Null,
    CollateralDisabled: IDL.Null,
    TaskError: IDL.Text,
    NoPrice: TokenType,
    WithdrawError: WithdrawError,
    UnhealthyPosition: Account,
    NoAccount: Account,
    TransferFromError: TransferFromError,
    ActionLimitExceeded: IDL.Record({ limit: IDL.Nat64 }),
    InsufficientSupply: IDL.Record({
      actual: IDL.Nat64,
      expected: IDL.Nat64,
    }),
    CallError: IDL.Text,
    BorrowError: BorrowError,
    NoPosition: TokenType,
    HealthyPosition: Account,
    InternalError: InternalError,
    StalePrice: IDL.Record({
      last_updated_ns: IDL.Nat64,
      token_type: TokenType,
    }),
    BalanceError: BalanceError,
    UnsupportedToken: TokenType,
    AccessError: IDL.Text,
  })
  const LedgerError = IDL.Variant({
    TransferError: TransferError,
    TransferFromError: TransferFromError,
    CallError: IDL.Text,
  })
  const TokenError = IDL.Variant({
    Overflow: IDL.Null,
    ConversionError: IDL.Null,
    UnsupportedToken: TokenType,
  })
  const BalanceActionError = IDL.Variant({
    Fee: IDL.Null,
    NoPrice: TokenType,
    LedgerError: LedgerError,
    InternalError: TokenError,
    StalePrice: IDL.Record({
      last_updated_ns: IDL.Nat64,
      token_type: TokenType,
    }),
    BalanceError: BalanceError,
    UnsupportedToken: TokenType,
    AccessError: IDL.Text,
  })
  const ActionError = IDL.Variant({
    Dex: DexError,
    Lending: LendingError,
    BalanceAction: BalanceActionError,
    ActionLimitExceeded: IDL.Record({ limit: IDL.Nat64 }),
    CallError: IDL.Text,
    AccessError: IDL.Text,
  })
  const AnyResult = IDL.Variant({ Ok: AnyResponse, Err: ActionError })
  const ActionsResult = IDL.Variant({
    Ok: IDL.Vec(AnyResult),
    Err: ActionError,
  })
  const TokenAmount = IDL.Record({
    fee_e8s: IDL.Nat64,
    amount_e8s: IDL.Nat64,
    price_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const BalanceEvent = IDL.Record({
    tx_id: IDL.Nat,
    to_account: Account,
    from_account: Account,
    amount: TokenAmount,
  })
  const BalanceResult = IDL.Variant({
    Ok: BalanceResponse,
    Err: BalanceActionError,
  })
  const AnchoredSpread = IDL.Variant({
    Bps100: IDL.Null,
    Bps200: IDL.Null,
    Bps50: IDL.Null,
  })
  const DexAnchoredBurnArgs = IDL.Record({
    shares: IDL.Nat,
    min_amount_0: IDL.Nat64,
    min_amount_1: IDL.Nat64,
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const DexAnchoredBurnResult = IDL.Record({
    amount_1_e8s: IDL.Nat64,
    fees_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
    fees_0_e8s: IDL.Nat64,
  })
  const DexAnchoredCollectArgs = IDL.Record({
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const DexAnchoredCollectResult = IDL.Record({
    fees_1_e8s: IDL.Nat64,
    fees_0_e8s: IDL.Nat64,
  })
  const DexAnchoredMintArgs = IDL.Record({
    amount_1_e8s: IDL.Nat64,
    min_shares: IDL.Nat,
    spread: AnchoredSpread,
    pool_id: PoolId,
    amount_0_e8s: IDL.Nat64,
  })
  const DexAnchoredMintResult = IDL.Record({
    shares: IDL.Nat,
    amount_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
  })
  const DexAnchoredPositionsArgs = IDL.Record({
    owner: IDL.Principal,
    pool_id: PoolId,
  })
  const DexAnchoredPositionInfo = IDL.Record({
    shares: IDL.Nat,
    pending_1_e8s: IDL.Nat64,
    pending_0_e8s: IDL.Nat64,
    spread: AnchoredSpread,
  })
  const DexBurnArgs = IDL.Record({
    min_amount_0: IDL.Nat64,
    min_amount_1: IDL.Nat64,
    lower_price_e8s: IDL.Nat,
    upper_price_e8s: IDL.Nat,
    pool_id: PoolId,
    amount: IDL.Nat,
  })
  const DexBurnResult = IDL.Record({
    amount_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
  })
  const DexClaimAllFeesArgs = IDL.Record({ pool_id: PoolId })
  const DexClaimFeesResult = IDL.Record({
    amount_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
  })
  const DexClaimFeesArgs = IDL.Record({
    lower_price_e8s: IDL.Nat,
    upper_price_e8s: IDL.Nat,
    pool_id: PoolId,
  })
  const DexBurnEvent = IDL.Record({
    lower_tick: IDL.Int32,
    upper_tick: IDL.Int32,
    owner: IDL.Principal,
    oracle_tick: IDL.Int32,
    pool_id: PoolId,
    amount: IDL.Nat,
  })
  const DexMintEvent = IDL.Record({
    lower_tick: IDL.Int32,
    upper_tick: IDL.Int32,
    owner: IDL.Principal,
    oracle_tick: IDL.Int32,
    pool_id: PoolId,
    amount: IDL.Nat,
  })
  const DexSwapEvent = IDL.Record({
    zero_for_one: IDL.Bool,
    fee_e8s: IDL.Nat64,
    oracle_tick: IDL.Opt(IDL.Int32),
    amount_out: IDL.Nat64,
    caller: IDL.Principal,
    amount_in: IDL.Nat64,
    pool_id: PoolId,
  })
  const DexCreatePoolEvent = IDL.Record({
    swap_fee_ppm: IDL.Nat32,
    tick: IDL.Int32,
    token0: TokenType,
    token1: TokenType,
    sqrt_price: IDL.Nat,
    pool_id: PoolId,
    protocol_fee_ppm: IDL.Nat32,
  })
  const AnchoredBurnEvent = IDL.Record({
    shares: IDL.Nat,
    fees_0: IDL.Nat64,
    fees_1: IDL.Nat64,
    owner: IDL.Principal,
    amount_0: IDL.Nat64,
    amount_1: IDL.Nat64,
    oracle_tick: IDL.Int32,
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const AnchoredMintEvent = IDL.Record({
    shares: IDL.Nat,
    owner: IDL.Principal,
    liquidity: IDL.Nat,
    amount_0: IDL.Nat64,
    amount_1: IDL.Nat64,
    oracle_tick: IDL.Int32,
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const DexCollectEvent = IDL.Record({
    lower_tick: IDL.Int32,
    upper_tick: IDL.Int32,
    owner: IDL.Principal,
    amount_0: IDL.Nat64,
    amount_1: IDL.Nat64,
    oracle_tick: IDL.Int32,
    pool_id: PoolId,
  })
  const CollectProtocolFeesEvent = IDL.Record({
    amount_0: IDL.Nat64,
    amount_1: IDL.Nat64,
    pool_id: IDL.Nat16,
  })
  const AnchoredCollectEvent = IDL.Record({
    fees_0: IDL.Nat64,
    fees_1: IDL.Nat64,
    owner: IDL.Principal,
    oracle_tick: IDL.Int32,
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const DexEvent = IDL.Variant({
    Burn: DexBurnEvent,
    Upgrade: IDL.Vec(DexUpgradeArg),
    Mint: DexMintEvent,
    Swap: DexSwapEvent,
    CreatePool: DexCreatePoolEvent,
    AnchoredBurn: AnchoredBurnEvent,
    AnchoredMint: AnchoredMintEvent,
    Collect: DexCollectEvent,
    CollectProtocolFees: CollectProtocolFeesEvent,
    AnchoredCollect: AnchoredCollectEvent,
  })
  const DexLiquidityDepthArgs = IDL.Record({ pool_id: PoolId })
  const DexTickLiquidity = IDL.Record({
    liquidity_net: IDL.Int,
    price_e8s: IDL.Nat,
  })
  const DexAnchoredBucketInfo = IDL.Record({
    lower_price_e8s: IDL.Nat,
    upper_price_e8s: IDL.Nat,
    liquidity: IDL.Nat,
    spread: AnchoredSpread,
  })
  const DexLiquidityDepthResult = IDL.Record({
    current_price_e8s: IDL.Nat,
    ticks: IDL.Vec(DexTickLiquidity),
    oracle_price_e8s: IDL.Nat,
    anchored_buckets: IDL.Vec(DexAnchoredBucketInfo),
    current_liquidity: IDL.Nat,
  })
  const DexMintArgs = IDL.Record({
    lower_price_e8s: IDL.Nat,
    upper_price_e8s: IDL.Nat,
    amount_1_e8s: IDL.Nat64,
    min_liquidity: IDL.Nat,
    pool_id: PoolId,
    amount_0_e8s: IDL.Nat64,
  })
  const DexMintResult = IDL.Record({
    amount_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
  })
  const DexPoolInfo = IDL.Record({
    token_0: TokenType,
    token_1: TokenType,
    swap_fee_ppm: IDL.Nat32,
    oracle_price_e8s: IDL.Nat,
    oracle_stale: IDL.Bool,
    pool_id: PoolId,
    protocol_fee_ppm: IDL.Nat32,
    price_e8s: IDL.Nat,
  })
  const DexPositionArgs = IDL.Record({
    lower_price_e8s: IDL.Nat,
    owner: IDL.Principal,
    upper_price_e8s: IDL.Nat,
    pool_id: PoolId,
  })
  const DexPositionInfo = IDL.Record({
    lower_price_e8s: IDL.Nat,
    pending_1_e8s: IDL.Nat64,
    upper_price_e8s: IDL.Nat,
    liquidity: IDL.Nat,
    pending_0_e8s: IDL.Nat64,
  })
  const DexPositionsArgs = IDL.Record({
    owner: IDL.Principal,
    pool_id: PoolId,
  })
  const DexQuoteArgs = IDL.Record({
    token_in: TokenType,
    amount_in: IDL.Nat64,
    pool_id: PoolId,
  })
  const DexQuoteResult = IDL.Record({
    fee_e8s: IDL.Nat64,
    amount_out: IDL.Nat64,
  })
  const DexQuoteAnchoredBurnArgs = IDL.Record({
    shares: IDL.Nat,
    owner: IDL.Principal,
    spread: AnchoredSpread,
    pool_id: PoolId,
  })
  const DexQuoteMintResult = IDL.Record({
    liquidity: IDL.Nat,
    amount_1_e8s: IDL.Nat64,
    amount_0_e8s: IDL.Nat64,
  })
  const ConsentMessageMetadata = IDL.Record({
    utc_offset_minutes: IDL.Opt(IDL.Int16),
    language: IDL.Text,
  })
  const ConsentMessageSpec = IDL.Record({
    metadata: ConsentMessageMetadata,
    device_spec: IDL.Opt(IDL.Variant({ GenericDisplay: IDL.Null, FieldsDisplay: IDL.Null })),
  })
  const ConsentMessageRequest = IDL.Record({
    arg: IDL.Vec(IDL.Nat8),
    method: IDL.Text,
    user_preferences: ConsentMessageSpec,
  })
  const ICRC21Value = IDL.Variant({
    Text: IDL.Record({ content: IDL.Text }),
    TokenAmount: IDL.Record({
      decimals: IDL.Nat8,
      amount: IDL.Nat64,
      symbol: IDL.Text,
    }),
    TimestampSeconds: IDL.Record({ amount: IDL.Nat64 }),
    DurationSeconds: IDL.Record({ amount: IDL.Nat64 }),
  })
  const ConsentMessage = IDL.Variant({
    FieldsDisplayMessage: IDL.Record({
      fields: IDL.Vec(IDL.Tuple(IDL.Text, ICRC21Value)),
      intent: IDL.Text,
    }),
    GenericDisplayMessage: IDL.Text,
  })
  const ConsentInfo = IDL.Record({
    metadata: ConsentMessageMetadata,
    consent_message: ConsentMessage,
  })
  const ErrorInfo = IDL.Record({ description: IDL.Text })
  const ICRC21Error = IDL.Variant({
    GenericError: IDL.Record({
      description: IDL.Text,
      error_code: IDL.Nat,
    }),
    InsufficientPayment: ErrorInfo,
    UnsupportedCanisterCall: ErrorInfo,
    ConsentMessageUnavailable: ErrorInfo,
  })
  const ConsentMessageResponse = IDL.Variant({
    Ok: ConsentInfo,
    Err: ICRC21Error,
  })
  const GetArchivesArgs = IDL.Record({ from: IDL.Opt(IDL.Principal) })
  const GetArchivesResult = IDL.Vec(
    IDL.Record({
      end: IDL.Nat,
      canister_id: IDL.Principal,
      start: IDL.Nat,
    })
  )
  const GetBlocksArgs = IDL.Vec(IDL.Record({ start: IDL.Nat, length: IDL.Nat }))
  ICRC3Value.fill(
    IDL.Variant({
      Int: IDL.Int,
      Map: IDL.Vec(IDL.Tuple(IDL.Text, ICRC3Value)),
      Nat: IDL.Nat,
      Blob: IDL.Vec(IDL.Nat8),
      Text: IDL.Text,
      Array: IDL.Vec(ICRC3Value),
    })
  )
  GetBlocksResult.fill(
    IDL.Record({
      log_length: IDL.Nat,
      blocks: IDL.Vec(IDL.Record({ id: IDL.Nat, block: ICRC3Value })),
      archived_blocks: IDL.Vec(
        IDL.Record({
          args: GetBlocksArgs,
          callback: IDL.Func([GetBlocksArgs], [GetBlocksResult], ['query']),
        })
      ),
    })
  )
  const DataCertificate = IDL.Record({
    certificate: IDL.Vec(IDL.Nat8),
    hash_tree: IDL.Vec(IDL.Nat8),
  })
  const ActionResult = IDL.Variant({ Ok: IDL.Nat64, Err: LendingError })
  const WithdrawEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const SupplyPoolEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const SettleDebtReserveEvent = IDL.Record({ account: Account })
  const InitArg = IDL.Record({
    xrc_canister_id: IDL.Principal,
    authorized_principals: IDL.Vec(IDL.Principal),
  })
  const ToggleCollateralEvent = IDL.Record({
    collateral_enabled: IDL.Bool,
    fee_e8s: IDL.Nat64,
    account: Account,
    token_type: TokenType,
  })
  const SupplyReserveEvent = IDL.Record({
    tx_id: IDL.Nat,
    account: Account,
    amount: TokenAmount,
  })
  const WithdrawPoolEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const SupplyDetails = IDL.Record({
    lrf_e8s: IDL.Nat64,
    ltv_e8s: IDL.Nat64,
    lt_e8s: IDL.Nat64,
    rf_e8s: IDL.Nat64,
    cap_e8s: IDL.Nat64,
    lb_e8s: IDL.Nat64,
  })
  const BorrowDetails = IDL.Record({
    rate: BorrowRate,
    cap_e8s: IDL.Nat64,
  })
  const TokenDetails = IDL.Record({
    id: IDL.Principal,
    typ: TokenType,
    decimals: IDL.Nat8,
    supply_details: SupplyDetails,
    name: IDL.Text,
    fee_e8s: IDL.Nat64,
    borrow_details: BorrowDetails,
    symbol: IDL.Text,
  })
  const RepayEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const BorrowEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const LiquidatePoolEvent = IDL.Record({
    collateral_amount: TokenAmount,
    debt_amount: TokenAmount,
    account: Account,
  })
  const SettleDebtEvent = IDL.Record({
    account: Account,
    from_account: Account,
  })
  const SupplyEvent = IDL.Record({
    account: Account,
    amount: TokenAmount,
  })
  const Event = IDL.Variant({
    Withdraw: WithdrawEvent,
    SupplyPool: SupplyPoolEvent,
    SettleDebtReserve: SettleDebtReserveEvent,
    Upgrade: IDL.Vec(UpgradeArg),
    Init: InitArg,
    ToggleCollateral: ToggleCollateralEvent,
    SupplyReserve: SupplyReserveEvent,
    WithdrawPool: WithdrawPoolEvent,
    AddToken: TokenDetails,
    Repay: RepayEvent,
    Borrow: BorrowEvent,
    LiquidatePool: LiquidatePoolEvent,
    SettleDebt: SettleDebtEvent,
    Supply: SupplyEvent,
  })
  const EventType = IDL.Variant({
    Withdraw: IDL.Null,
    SupplyPool: IDL.Null,
    SettleDebtReserve: IDL.Null,
    Upgrade: IDL.Null,
    Init: IDL.Null,
    ToggleCollateral: IDL.Null,
    SupplyReserve: IDL.Null,
    WithdrawPool: IDL.Null,
    AddToken: IDL.Null,
    Repay: IDL.Null,
    Borrow: IDL.Null,
    LiquidatePool: IDL.Null,
    SettleDebt: IDL.Null,
    Supply: IDL.Null,
  })
  const Position = IDL.Record({
    supply_amount_e8s: IDL.Nat64,
    collateral_enabled: IDL.Bool,
    principal_debt: IDL.Nat64,
    supply_shares: IDL.Nat64,
    pool_amount: IDL.Nat64,
  })
  const SupplyReserveArgs = IDL.Record({
    subaccount: IDL.Opt(Subaccount),
    amount_e8s: IDL.Nat64,
    token_type: TokenType,
  })
  const TokenStats = IDL.Record({
    total_reserves_e8s: IDL.Nat64,
    total_pool_repaid_e8s: IDL.Nat64,
    total_supply_e8s: IDL.Nat64,
    total_pool_bonus_e8s: IDL.Nat64,
    total_shares_e8s: IDL.Nat64,
    total_collateral_shares_e8s: IDL.Nat64,
    total_collateral_e8s: IDL.Nat64,
    borrow_rate_e8s: IDL.Nat64,
    borrow_index: IDL.Nat64,
    total_pool_bonus_value_e8s: IDL.Nat64,
    total_pool_liquidated_e8s: IDL.Nat64,
    utilization_rate_e8s: IDL.Nat64,
    price_last_updated_ns: IDL.Nat64,
    supply_rate_e8s: IDL.Nat64,
    total_borrowed_e8s: IDL.Nat64,
    price_e8s: IDL.Nat64,
    total_pool_supply_e8s: IDL.Nat64,
    unallocated_e8s: IDL.Nat64,
    action_fee_e8s: IDL.Nat64,
  })
  const ActionDetails = IDL.Record({
    action_value_fee_e8s: IDL.Nat64,
    bulk_async_action_limit: IDL.Nat32,
    bulk_action_limit: IDL.Nat32,
  })
  return IDL.Service({
    actions: IDL.Func([ActionsArgs], [ActionsResult], []),
    balance_all_of: IDL.Func([Account], [IDL.Vec(IDL.Tuple(TokenType, IDL.Nat))], ['query']),
    balance_event_count: IDL.Func([], [IDL.Nat64], ['query']),
    balance_events_list: IDL.Func(
      [IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, BalanceEvent))],
      ['query']
    ),
    balance_events_list_by_token_type: IDL.Func(
      [TokenType, IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, BalanceEvent))],
      ['query']
    ),
    balance_events_list_by_user: IDL.Func(
      [Account, IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, BalanceEvent))],
      ['query']
    ),
    balance_of: IDL.Func([TokenType, Account], [IDL.Nat], ['query']),
    balance_of_e8s: IDL.Func([TokenType, Account], [IDL.Nat64], ['query']),
    balance_supply: IDL.Func([BalanceSupplyArgs], [BalanceResult], []),
    balance_withdraw: IDL.Func([BalanceWithdrawArgs], [BalanceResult], []),
    config_events_count: IDL.Func([], [IDL.Nat64], ['query']),
    dex_anchored_burn: IDL.Func(
      [DexAnchoredBurnArgs],
      [IDL.Variant({ Ok: DexAnchoredBurnResult, Err: DexError })],
      []
    ),
    dex_anchored_collect: IDL.Func(
      [DexAnchoredCollectArgs],
      [IDL.Variant({ Ok: DexAnchoredCollectResult, Err: DexError })],
      []
    ),
    dex_anchored_mint: IDL.Func(
      [DexAnchoredMintArgs],
      [IDL.Variant({ Ok: DexAnchoredMintResult, Err: DexError })],
      []
    ),
    dex_anchored_positions: IDL.Func(
      [DexAnchoredPositionsArgs],
      [IDL.Vec(DexAnchoredPositionInfo)],
      ['query']
    ),
    dex_burn: IDL.Func([DexBurnArgs], [IDL.Variant({ Ok: DexBurnResult, Err: DexError })], []),
    dex_claim_all_fees: IDL.Func(
      [DexClaimAllFeesArgs],
      [IDL.Variant({ Ok: IDL.Vec(DexClaimFeesResult), Err: DexError })],
      []
    ),
    dex_claim_fees: IDL.Func(
      [DexClaimFeesArgs],
      [IDL.Variant({ Ok: DexClaimFeesResult, Err: DexError })],
      []
    ),
    dex_events: IDL.Func(
      [IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, DexEvent))],
      ['query']
    ),
    dex_events_count: IDL.Func([], [IDL.Nat64], ['query']),
    dex_liquidity_depth: IDL.Func(
      [DexLiquidityDepthArgs],
      [IDL.Variant({ Ok: DexLiquidityDepthResult, Err: DexError })],
      ['query']
    ),
    dex_mint: IDL.Func([DexMintArgs], [IDL.Variant({ Ok: DexMintResult, Err: DexError })], []),
    dex_pools: IDL.Func([], [IDL.Vec(DexPoolInfo)], ['query']),
    dex_position: IDL.Func(
      [DexPositionArgs],
      [IDL.Variant({ Ok: DexPositionInfo, Err: DexError })],
      ['query']
    ),
    dex_positions: IDL.Func([DexPositionsArgs], [IDL.Vec(DexPositionInfo)], ['query']),
    dex_quote: IDL.Func(
      [DexQuoteArgs],
      [IDL.Variant({ Ok: DexQuoteResult, Err: DexError })],
      ['query']
    ),
    dex_quote_anchored_burn: IDL.Func(
      [DexQuoteAnchoredBurnArgs],
      [IDL.Variant({ Ok: DexAnchoredBurnResult, Err: DexError })],
      ['query']
    ),
    dex_quote_anchored_mint: IDL.Func(
      [DexAnchoredMintArgs],
      [IDL.Variant({ Ok: DexAnchoredMintResult, Err: DexError })],
      ['query']
    ),
    dex_quote_burn: IDL.Func(
      [DexBurnArgs],
      [IDL.Variant({ Ok: DexBurnResult, Err: DexError })],
      ['query']
    ),
    dex_quote_mint: IDL.Func(
      [DexMintArgs],
      [IDL.Variant({ Ok: DexQuoteMintResult, Err: DexError })],
      ['query']
    ),
    dex_swap: IDL.Func([DexSwapArgs], [IDL.Variant({ Ok: DexSwapResult, Err: DexError })], []),
    icrc10_supported_standards: IDL.Func(
      [],
      [IDL.Vec(IDL.Record({ url: IDL.Text, name: IDL.Text }))],
      ['query']
    ),
    icrc21_canister_call_consent_message: IDL.Func(
      [ConsentMessageRequest],
      [ConsentMessageResponse],
      []
    ),
    icrc3_get_archives: IDL.Func([GetArchivesArgs], [GetArchivesResult], ['query']),
    icrc3_get_blocks: IDL.Func([GetBlocksArgs], [GetBlocksResult], ['query']),
    icrc3_get_tip_certificate: IDL.Func([], [IDL.Opt(DataCertificate)], ['query']),
    icrc3_supported_block_types: IDL.Func(
      [],
      [IDL.Vec(IDL.Record({ url: IDL.Text, block_type: IDL.Text }))],
      ['query']
    ),
    lending_borrow: IDL.Func([BorrowArgs], [ActionResult], []),
    lending_events_count: IDL.Func([], [IDL.Nat64], ['query']),
    lending_events_list: IDL.Func(
      [IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, Event))],
      ['query']
    ),
    lending_events_list_by_token_type: IDL.Func(
      [TokenType, IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, Event))],
      ['query']
    ),
    lending_events_list_by_type: IDL.Func(
      [EventType, IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, Event))],
      ['query']
    ),
    lending_events_list_by_user: IDL.Func(
      [Account, IDL.Int64, IDL.Nat64],
      [IDL.Vec(IDL.Tuple(IDL.Nat64, Event))],
      ['query']
    ),
    lending_events_max_query_count: IDL.Func([], [IDL.Nat64], ['query']),
    lending_position_all_of: IDL.Func(
      [Account],
      [IDL.Vec(IDL.Tuple(TokenType, Position))],
      ['query']
    ),
    lending_position_all_unhealthy: IDL.Func(
      [],
      [IDL.Vec(IDL.Tuple(Account, IDL.Vec(IDL.Tuple(TokenType, Position))))],
      ['query']
    ),
    lending_position_of: IDL.Func([TokenType, Account], [IDL.Opt(Position)], ['query']),
    lending_repay: IDL.Func([RepayArgs], [ActionResult], []),
    lending_supply: IDL.Func([SupplyArgs], [ActionResult], []),
    lending_supply_pool: IDL.Func([SupplyPoolArgs], [ActionResult], []),
    lending_supply_reserve: IDL.Func([SupplyReserveArgs], [ActionResult], []),
    lending_toggle_collateral: IDL.Func([ToggleCollateralArgs], [ActionResult], []),
    lending_tokens_borrow_index_of: IDL.Func([TokenType], [IDL.Opt(IDL.Nat64)], ['query']),
    lending_tokens_borrowed_of: IDL.Func([TokenType], [IDL.Opt(IDL.Nat64)], ['query']),
    lending_tokens_details: IDL.Func([], [IDL.Vec(TokenDetails)], ['query']),
    lending_tokens_details_of: IDL.Func([TokenType], [IDL.Opt(TokenDetails)], ['query']),
    lending_tokens_reserve_of: IDL.Func([TokenType], [IDL.Opt(IDL.Nat64)], ['query']),
    lending_tokens_stats: IDL.Func([], [IDL.Vec(IDL.Tuple(TokenType, TokenStats))], ['query']),
    lending_tokens_stats_of: IDL.Func([TokenType], [IDL.Opt(TokenStats)], ['query']),
    lending_tokens_supply_of: IDL.Func(
      [TokenType],
      [IDL.Opt(IDL.Tuple(IDL.Nat64, IDL.Nat64))],
      ['query']
    ),
    lending_tokens_types: IDL.Func([], [IDL.Vec(TokenType)], ['query']),
    lending_utils_accounts_of: IDL.Func([IDL.Principal], [IDL.Vec(Account)], ['query']),
    lending_utils_hf_of: IDL.Func([Account], [IDL.Opt(IDL.Nat64)], ['query']),
    lending_withdraw: IDL.Func([WithdrawArgs], [ActionResult], []),
    lending_withdraw_pool: IDL.Func([WithdrawPoolArgs], [ActionResult], []),
    utils_action_details: IDL.Func([], [ActionDetails], ['query']),
    utils_git_commit_id: IDL.Func([], [IDL.Text], ['query']),
    utils_oracle_prices: IDL.Func(
      [],
      [IDL.Vec(IDL.Tuple(TokenType, IDL.Nat64, IDL.Nat64))],
      ['query']
    ),
    utils_task_queue: IDL.Func([], [IDL.Vec(IDL.Tuple(IDL.Nat64, IDL.Text))], ['query']),
  })
}
export const init = ({ IDL }) => {
  const PoolId = IDL.Nat16
  const DexUpgradeArg = IDL.Variant({
    ProtocolFeePpm: IDL.Record({
      pool_id: PoolId,
      protocol_fee_ppm: IDL.Nat32,
    }),
    SwapFeePpm: IDL.Record({
      swap_fee_ppm: IDL.Nat32,
      pool_id: PoolId,
    }),
  })
  const UpgradeSupplyDetails = IDL.Variant({
    LB: IDL.Nat64,
    LT: IDL.Nat64,
    RF: IDL.Nat64,
    Cap: IDL.Nat64,
    LRF: IDL.Nat64,
    LTV: IDL.Nat64,
  })
  const TokenType = IDL.Variant({
    ICP: IDL.Null,
    WTN: IDL.Null,
    USDC: IDL.Null,
    USDT: IDL.Null,
    ckBTC: IDL.Null,
    nICP: IDL.Null,
    ckDOGE: IDL.Null,
    YUSAN: IDL.Null,
  })
  const KinkUtilizationCurveParams = IDL.Record({
    rate_start_e8s: IDL.Opt(IDL.Nat64),
    rate_kink_e8s: IDL.Nat64,
    kink_utilization_e8s: IDL.Nat64,
    rate_end_e8s: IDL.Nat64,
  })
  const BorrowRate = IDL.Variant({
    Kink: KinkUtilizationCurveParams,
    Fixed: IDL.Nat64,
  })
  const UpgradeBorrowDetails = IDL.Variant({
    Cap: IDL.Nat64,
    BorrowRate: BorrowRate,
  })
  const UpgradeArg = IDL.Variant({
    PriceRetryDelaySecs: IDL.Nat64,
    UpgradeSupplyDetails: IDL.Record({
      details: UpgradeSupplyDetails,
      token_type: TokenType,
    }),
    ActionValueFeeE8S: IDL.Nat64,
    UpgradeBorrowDetails: IDL.Record({
      details: UpgradeBorrowDetails,
      token_type: TokenType,
    }),
    PriceIntervalSecs: IDL.Nat64,
  })
  const ConfigUpgradeArg = IDL.Variant({
    PriceRetryDelaySecs: IDL.Nat64,
    ActionValueFeeE8S: IDL.Nat64,
    PriceMaxAgeMultiplier: IDL.Nat64,
    PriceIntervalSecs: IDL.Nat64,
  })
  const CanisterUpgradeArg = IDL.Variant({
    Dex: DexUpgradeArg,
    Lending: UpgradeArg,
    Config: ConfigUpgradeArg,
  })
  const CanisterInitArg = IDL.Record({
    xrc_canister_id: IDL.Principal,
    authorized_principals: IDL.Vec(IDL.Principal),
    upgrade_args: IDL.Vec(CanisterUpgradeArg),
  })
  const Args = IDL.Variant({
    Upgrade: IDL.Vec(CanisterUpgradeArg),
    Init: CanisterInitArg,
  })
  return [Args]
}
